"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import { ArrowForward, Launch } from "@mui/icons-material";
import Link from "next/link";
import { CaseStudy } from "@/types/case-study";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { shades } from "@/styles/theme";
import CaseStudyVideoShowcase from "../YouTube";

export const runtime = "edge";

export default function CaseStudies() {
  const theme = useTheme();
  const [caseStudiesList, setCaseStudiesList] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchCaseStudies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "caseStudies"),
        where("isActive", "==", true)
      );

      const querySnapshot = await getDocs(q);
      const caseStudiesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        slug: doc.data().slug || doc.id,
        title: doc.data().title || "Untitled Case Study",
        client: doc.data().client || "",
        excerpt: doc.data().excerpt || "",
        industry: doc.data().industry || "",
        services: doc.data().services || [],
        duration: doc.data().duration || "",
        projectUrl: doc.data().projectUrl || "",
        featuredImage: doc.data().featuredImage || "",
        isFeatured: doc.data().isFeatured || false,
        challenge: doc.data().challenge || "",
        solution: doc.data().solution || {
          overview: "",
          steps: [],
        },
        results: doc.data().results || {
          metrics: [],
          additionalResults: [],
        },
        keyLearnings: doc.data().keyLearnings || [],
        testimonial: doc.data().testimonial || {
          quote: "",
          author: "",
          position: "",
          company: "",
        },
        gallery: doc.data().gallery || [],
        isActive: doc.data().isActive ?? true,
        createdAt: doc.data().createdAt || new Date().toISOString(),
        updatedAt: doc.data().updatedAt || new Date().toISOString(),
      })) as CaseStudy[];

      // Sort client-side to ensure documents without a createdAt field are still returned and sorted
      const sortedCaseStudies = caseStudiesData.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setCaseStudiesList(sortedCaseStudies);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to load blog posts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaseStudies();
  }, [fetchCaseStudies, retryCount]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ pt: 20, pb: 8 }}>
        <Skeleton variant="rectangular" width="100%" height={100} />
        <Skeleton variant="text" sx={{ fontSize: "3rem", mb: 4 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ pt: 20, pb: 8 }}>
        <Typography variant="h4" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setRetryCount((prev) => prev + 1);
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Box pb={8}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 3,
              }}
            >
              Our Work Speaks Volumes
            </Typography>
            <Typography
              variant="h5"
              color={`textSecondary`}
              sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
            >
              Explore real-world examples of how we&apos;ve helped businesses
              transform their digital presence, drive growth, and achieve
              measurable results.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Case Studies Grid */}
        <Grid container spacing={4}>
          {caseStudiesList.map((caseStudy) => (
            <Grid key={caseStudy.id} size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="240"
                  image={caseStudy.featuredImage}
                  alt={caseStudy.title}
                  sx={{
                    objectFit: "cover",
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                />
                <CardContent
                  sx={{
                    p: 4,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={caseStudy.industry}
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 2,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, flexGrow: 1 }}
                  >
                    {caseStudy.title}
                  </Typography>

                  <Typography
                    color={`${shades.secondary[600]}`}
                    sx={{ mb: 3, flexGrow: 1 }}
                  >
                    {caseStudy.excerpt}
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                  >
                    {caseStudy.services.map((service, index) => (
                      <Chip
                        key={index}
                        label={service}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: "text.primary",
                          fontSize: "0.75rem",
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
                    <Button
                      component={Link}
                      href={`/case-studies/${caseStudy.slug}`}
                      variant="contained"
                      startIcon={<ArrowForward />}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        flex: 1,
                      }}
                    >
                      Read Case Study
                    </Button>
                    <Button
                      component={Link}
                      href={caseStudy.projectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      startIcon={<Launch />}
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      }}
                    >
                      Live Site
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {caseStudiesList.length === 0 && (
          <Box textAlign="center" sx={{ py: 8 }}>
            <Typography variant="h5" color="text.secondary">
              More case studies coming soon...
            </Typography>
          </Box>
        )}

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: "center",
            mt: 8,
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Be Our Next Success Story?
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
          >
            Let&apos;s work together to create something amazing. Your project
            could be featured here next!
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/contact"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              px: 4,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Start Your Project
          </Button>
        </Box>

        {/* <YouTubeEmbed
          videoId="dQw4w9WgXcQ"
          title="LinXar Labs - Automation Solutions Overview"
          privacy={true}
        /> */}

        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12">
          <CaseStudyVideoShowcase />
        </div>
      </Container>
    </Box>
  );
}
